import KpiChart from "@app/components/kpi-chart";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import { dateRangeFromISO, useDateRange } from "@app/lib/date-range";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useRef, useState } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import type {
  stakeholderTxnsDialog_Query,
  stakeholderTxnsDialog_Query$data,
} from "@app/queries/stakeholderTxnsDialog_Query.graphql";
import type {
  stakeholderTxnsDialog_ClassifyTxnsMutation,
  TxnClassificationEnum,
} from "@app/queries/stakeholderTxnsDialog_ClassifyTxnsMutation.graphql";
import type { stakeholderTxnsDialog_AttributeTxnsMutation } from "@app/queries/stakeholderTxnsDialog_AttributeTxnsMutation.graphql";
import type { stakeholderTxnsDialog_CreateRuleMutation } from "@app/queries/stakeholderTxnsDialog_CreateRuleMutation.graphql";
import type { stakeholderTxnsDialog_SetTxnsNeedsReviewMutation } from "@app/queries/stakeholderTxnsDialog_SetTxnsNeedsReviewMutation.graphql";
import type { stakeholderTxnsDialog_AssignTxnNatureMutation } from "@app/queries/stakeholderTxnsDialog_AssignTxnNatureMutation.graphql";
import { ProvenanceBadge } from "@app/pages/transactions/provenance-badge";
import { useToast } from "@app/components/toast";

export const dialogQuery = graphql`
  query stakeholderTxnsDialog_Query(
    $from: String
    $accountNumber: String
    $categorized: Boolean
    $perspectiveId: ID
    $counterpartyId: ID
    $query: String
  ) {
    transactions(
      from: $from
      accountNumber: $accountNumber
      categorized: $categorized
      perspectiveId: $perspectiveId
      counterpartyId: $counterpartyId
      query: $query
    ) {
      totalCount
      limit
      accounts {
        number
        nickname
      }
      health {
        period
        categorized
        uncategorized
        internal
      }
      txns {
        txn {
          id
        }
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        otherBankAccount {
          number
        }
        classification
        expenseNature {
          id
          name
        }
        categoryNode {
          id
          name
        }
        attribution {
          source
          performedAt
          user {
            email
          }
          rule {
            regex
          }
        }
      }
    }
    allStakeholders {
      id
      name
    }
    expenseNatures {
      id
      name
    }
  }
`;

const classifyMutation = graphql`
  mutation stakeholderTxnsDialog_ClassifyTxnsMutation(
    $input: ClassifyTxnsInputObject!
  ) {
    classifyTxns(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const assignNatureMutation = graphql`
  mutation stakeholderTxnsDialog_AssignTxnNatureMutation(
    $input: AssignTxnNatureInputObject!
  ) {
    assignTxnNature(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const attributeMutation = graphql`
  mutation stakeholderTxnsDialog_AttributeTxnsMutation(
    $input: AttributeTxnsInputObject!
  ) {
    attributeTxns(input: $input) {
      stakeholder {
        id
        name
      }
      suggestion {
        regex
        matchKind
        additionalCount
        totalCount
      }
      errors {
        message
      }
    }
  }
`;

const createRuleMutation = graphql`
  mutation stakeholderTxnsDialog_CreateRuleMutation(
    $input: CreateRuleInputObject!
  ) {
    createRule(input: $input) {
      rule {
        id
      }
      errors {
        message
      }
    }
  }
`;

const setNeedsReviewMutation = graphql`
  mutation stakeholderTxnsDialog_SetTxnsNeedsReviewMutation(
    $input: SetTxnsNeedsReviewInputObject!
  ) {
    setTxnsNeedsReview(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

type Data = stakeholderTxnsDialog_Query$data["transactions"];
type Row = NonNullable<Data>["txns"][number];

// The three transaction kinds, each an independent toggle. `activeClass` mirrors
// the amount-column coloring (cost = red, revenue = green, internal = gray).
type FlowKind = "INTERNAL" | "COST" | "REVENUE";
const FLOW_FILTERS: { key: FlowKind; label: string; activeClass: string }[] = [
  { key: "INTERNAL", label: "Internal", activeClass: "bg-gray-500 text-white border-gray-500" },
  { key: "COST", label: "Cost", activeClass: "bg-red-600 text-white border-red-600" },
  { key: "REVENUE", label: "Revenue", activeClass: "bg-green-600 text-white border-green-600" },
];

const kindOf = (txn: { internal: boolean; amount: number }): FlowKind =>
  txn.internal ? "INTERNAL" : txn.amount < 0 ? "COST" : "REVENUE";

const CLASSIFICATIONS: { value: TxnClassificationEnum; label: string }[] = [
  { value: "FRAUDULENT", label: "Fraudulent" },
  { value: "LOAN_PROCEEDS", label: "Loan proceeds" },
  { value: "LOAN_REPAYMENT", label: "Loan repayment" },
  { value: "DIVIDEND", label: "Dividend" },
  { value: "SALARY", label: "Salary / wages" },
];

// Record<string, …> rather than Record<TxnClassificationEnum, …>: Relay enum
// types carry a synthetic "%future added value" member we don't want to map.
const CLASSIFICATION_BADGE: Record<string, string> = {
  FRAUDULENT: "bg-red-100 text-red-700",
  LOAN_PROCEEDS: "bg-violet-100 text-violet-700",
  LOAN_REPAYMENT: "bg-violet-100 text-violet-700",
  DIVIDEND: "bg-amber-100 text-amber-800",
  SALARY: "bg-slate-100 text-slate-600",
};

const classificationLabel = (value: TxnClassificationEnum) =>
  CLASSIFICATIONS.find((c) => c.value === value)?.label ?? value;

const money2 = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

const monthLabel = (month: string) =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });

export default function StakeholderTxnsDialog({
  stakeholder,
  perspectiveId,
  onClose,
  onChanged,
}: {
  stakeholder: { id: string; name: string };
  perspectiveId: string | null;
  onClose: () => void;
  // Called after each mutation so the launching surface (e.g. the sidepanel
  // count, U8) can re-fetch — it lives in a separate query refreshInPlace can't touch.
  onChanged?: () => void;
}) {
  const range = useDateRange();
  const from = dateRangeFromISO(range);
  const notify = useToast();

  // Server-side filters (refetch on change). Counterparty is fixed to this
  // stakeholder, so there is no counterparty filter (R7).
  const [accountNumber, setAccountNumber] = useState<string | null>(null);
  const [categorized, setCategorized] = useState<boolean | null>(null);
  const [queryText, setQueryText] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Chart-driven month selection — a CLIENT-side sub-filter within the loaded
  // range (the query has no bounded upper month, R5/R6/R8).
  const [monthRange, setMonthRange] = useState<{ from: string; to: string } | null>(null);

  // Independent per-kind visibility toggles, applied CLIENT-side. Internal
  // transfers (own-account transfers and the Wise/Deel funding legs) are hidden
  // by default; cost and revenue are shown. Any combination is allowed.
  const [flowsShown, setFlowsShown] = useState<Record<FlowKind, boolean>>({
    INTERNAL: false,
    COST: true,
    REVENUE: true,
  });

  const [data, setData] = useState<Data | null>(null);
  const [allStakeholders, setAllStakeholders] = useState<
    stakeholderTxnsDialog_Query$data["allStakeholders"]
  >([]);
  const [natures, setNatures] = useState<
    stakeholderTxnsDialog_Query$data["expenseNatures"]
  >([]);
  const [natureSort, setNatureSort] = useState<"none" | "asc" | "desc">("none");
  const [busy, setBusy] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [viaRule, setViaRule] = useState(false);
  const descriptionRef = useRef<HTMLInputElement>(null);

  // Debounce the description filter (matches the transactions page, 300ms).
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(queryText), 300);
    return () => clearTimeout(handle);
  }, [queryText]);

  useEffect(() => {
    setData(null);
    const subscription = fetchQuery<stakeholderTxnsDialog_Query>(
      environment,
      dialogQuery,
      {
        from: from ?? undefined,
        accountNumber: accountNumber ?? undefined,
        categorized: categorized ?? undefined,
        perspectiveId,
        counterpartyId: stakeholder.id,
        query: debouncedQuery || undefined,
      }
    ).subscribe({
      next: (result) => {
        setData(result.transactions);
        setAllStakeholders(result.allStakeholders ?? []);
        setNatures(result.expenseNatures ?? []);
      },
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeholder.id, perspectiveId, from, accountNumber, categorized, debouncedQuery]);

  const allTxns = data?.txns ?? [];
  // Client-side sub-filters over the loaded set: the per-kind flow toggles and
  // the chart-driven month range.
  const filtered = allTxns.filter((t) => {
    if (!flowsShown[kindOf(t)]) return false;
    if (monthRange) {
      const month = t.postingDate.slice(0, 7);
      if (month < monthRange.from || month > monthRange.to) return false;
    }
    return true;
  });
  // Optional client-side sort by expense nature (empties last regardless of direction).
  const txns =
    natureSort === "none"
      ? filtered
      : [...filtered].sort((a, b) => {
          const an = a.expenseNature?.name ?? "";
          const bn = b.expenseNature?.name ?? "";
          if (an === "" || bn === "") return an === bn ? 0 : an === "" ? 1 : -1;
          const cmp = an.localeCompare(bn);
          return natureSort === "asc" ? cmp : -cmp;
        });

  const health = data?.health ?? [];
  const series = [
    {
      name: "Categorized",
      type: "bar" as const,
      stack: "h",
      color: "#0f766e",
      points: health.map((h) => ({ month: h.period, value: h.categorized })),
    },
    {
      name: "Uncategorized",
      type: "bar" as const,
      stack: "h",
      color: "#f59e0b",
      points: health.map((h) => ({ month: h.period, value: h.uncategorized })),
    },
    {
      name: "Internal",
      type: "bar" as const,
      stack: "h",
      color: "#9ca3af",
      points: health.map((h) => ({ month: h.period, value: h.internal })),
    },
  ];

  // Multi-select with shift-extend (anchor captured before setState — the
  // transactions-page pattern; lastIndexRef is reassigned synchronously).
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const lastIndexRef = useRef<number | null>(null);
  const shiftHeldRef = useRef(false);

  const toggleRow = (txnId: string, index: number) => {
    const anchor = lastIndexRef.current;
    lastIndexRef.current = index;
    setSelected((prev) => {
      const next = new Set(prev);
      if (shiftHeldRef.current && anchor != null) {
        const [lo, hi] = anchor < index ? [anchor, index] : [index, anchor];
        for (let i = lo; i <= hi; i++) next.add(txns[i].txn.id);
      } else if (next.has(txnId)) {
        next.delete(txnId);
      } else {
        next.add(txnId);
      }
      return next;
    });
  };

  // Re-fetch the dialog query in place after a mutation, and notify the
  // launching surface so its own (separate) query refreshes too (R20).
  const refreshInPlace = () => {
    fetchQuery<stakeholderTxnsDialog_Query>(environment, dialogQuery, {
      from: from ?? undefined,
      accountNumber: accountNumber ?? undefined,
      categorized: categorized ?? undefined,
      perspectiveId,
      counterpartyId: stakeholder.id,
      query: debouncedQuery || undefined,
    }).subscribe({
      next: (result) => {
        setData(result.transactions);
        setAllStakeholders(result.allStakeholders ?? []);
        setNatures(result.expenseNatures ?? []);
        onChanged?.();
      },
    });
  };

  const afterMutation = () => {
    setSelected(new Set());
    setBusy(false);
    refreshInPlace();
  };

  const classify = (classification: TxnClassificationEnum | null) => {
    setBusy(true);
    commitMutation<stakeholderTxnsDialog_ClassifyTxnsMutation>(environment, {
      mutation: classifyMutation,
      variables: { input: { txnIds: [...selected], classification } },
      onCompleted: afterMutation,
      onError: () => {
        setBusy(false);
        notify({ message: "Reclassify failed — please try again.", kind: "error" });
      },
    });
  };

  // Bulk-tag (or clear) the expense nature on the selection. Null clears it.
  const assignNature = (expenseNatureId: string | null) => {
    setBusy(true);
    commitMutation<stakeholderTxnsDialog_AssignTxnNatureMutation>(environment, {
      mutation: assignNatureMutation,
      variables: { input: { txnIds: [...selected], expenseNatureId } },
      onCompleted: afterMutation,
      onError: () => {
        setBusy(false);
        notify({ message: "Tag nature failed — please try again.", kind: "error" });
      },
    });
  };

  // Send the selection to the needs-review worklist — the manual op_needs_review
  // override. The flagged txns leave the operating corpus and surface on /needs-review,
  // where their internal counterparty account is assigned.
  const sendToWorklist = () => {
    const count = selected.size;
    setBusy(true);
    commitMutation<stakeholderTxnsDialog_SetTxnsNeedsReviewMutation>(environment, {
      mutation: setNeedsReviewMutation,
      variables: { input: { txnIds: [...selected], needsReview: true } },
      onCompleted: () => {
        afterMutation();
        notify({
          message: `Sent ${count} to the needs-review worklist.`,
          kind: "success",
        });
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Send to worklist failed — please try again.", kind: "error" });
      },
    });
  };

  // Create the dataset-wide rule from a reassignment's suggestion. Separate so it
  // can fire from the action toast below — the rule is only created when the
  // operator clicks "Create rule", keeping the global re-attribution deliberate (R16).
  const createRuleFromSuggestion = (
    suggestion: { regex: string; matchKind: string; totalCount: number },
    target: { id: string }
  ) => {
    commitMutation<stakeholderTxnsDialog_CreateRuleMutation>(environment, {
      mutation: createRuleMutation,
      variables: {
        input: {
          regex: suggestion.regex,
          matchKind: suggestion.matchKind,
          stakeholderId: target.id,
          perspectiveId,
        },
      },
      onCompleted: () => {
        notify({ message: "Rule created and applied.", kind: "success" });
        refreshInPlace();
      },
      onError: () => {
        // The rule exists; recapture may still be running past the request.
        notify({
          message: "Rule created — re-attribution may be incomplete; refresh shortly.",
          kind: "info",
        });
        refreshInPlace();
      },
    });
  };

  // Reassign the selection to another stakeholder. When `viaRule` is set, the
  // attribute call returns a pattern suggestion; rather than a blocking confirm,
  // we surface the dataset-wide rule as a non-blocking action toast — nothing
  // happens until the operator clicks "Create rule" (R16).
  const reassign = (choice: { stakeholderId?: string; name?: string }) => {
    const count = selected.size;
    const createRule = viaRule;
    setBusy(true);
    setReassignOpen(false);
    commitMutation<stakeholderTxnsDialog_AttributeTxnsMutation>(environment, {
      mutation: attributeMutation,
      variables: {
        input: {
          txnIds: [...selected],
          stakeholderId: choice.stakeholderId,
          name: choice.name,
          perspectiveId,
        },
      },
      onCompleted: (response) => {
        const suggestion = response.attributeTxns?.suggestion;
        const target = response.attributeTxns?.stakeholder;
        afterMutation();
        if (createRule && suggestion && target) {
          notify({
            message: `Reassigned ${count}. Also create a rule for “${suggestion.regex}”? It re-attributes all ${suggestion.totalCount} matching across every account.`,
            action: {
              label: "Create rule",
              onClick: () => createRuleFromSuggestion(suggestion, target),
            },
          });
        } else if (target) {
          notify({
            message: `Reassigned ${count} to ${target.name}.`,
            kind: "success",
          });
        }
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Reassign failed — please try again.", kind: "error" });
      },
    });
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 z-50" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[90vh] w-[90vw] bg-white shadow-2xl z-50 rounded-lg flex flex-col focus:outline-none"
          onOpenAutoFocus={(event) => {
            // Land focus on the description filter, not the close button.
            event.preventDefault();
            descriptionRef.current?.focus();
          }}
        >
          <div className="flex items-center gap-3 p-4 border-b border-gray-100">
            <Dialog.Title className="text-lg font-bold text-gray-900 truncate flex-1">
              {stakeholder.name}
              <span className="ml-2 text-sm font-normal text-gray-400">
                {txns.length.toLocaleString()} transactions
              </span>
            </Dialog.Title>
            <Dialog.Close className="shrink-0 text-2xl leading-none text-gray-400 hover:text-gray-700 px-1">
              ×
            </Dialog.Close>
          </div>

          {data === null ? (
            <div className="flex-1 p-6 space-y-3">
              <div className="h-[260px] bg-gray-50 rounded animate-pulse" />
              <div className="h-8 bg-gray-50 rounded animate-pulse" />
              <div className="h-64 bg-gray-50 rounded animate-pulse" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <KpiChart
                title="Transaction health"
                series={series}
                format="money"
                onMonthClick={(month) => setMonthRange({ from: month, to: month })}
                onMonthRange={(a, b) => setMonthRange({ from: a, to: b })}
              />

              {/* Filters — no counterparty (single stakeholder). */}
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <input
                  ref={descriptionRef}
                  type="text"
                  placeholder="Filter description…"
                  value={queryText}
                  onChange={(event) => setQueryText(event.target.value)}
                  className="border rounded px-2 py-1 w-56"
                />
                <select
                  value={accountNumber ?? ""}
                  onChange={(event) => setAccountNumber(event.target.value || null)}
                  className="border rounded px-2 py-1"
                >
                  <option value="">All accounts</option>
                  {(data?.accounts ?? []).map((account) => (
                    <option key={account.number} value={account.number}>
                      {account.nickname || account.number}
                    </option>
                  ))}
                </select>
                {/* Per-kind visibility — each toggles independently. */}
                <div className="inline-flex items-center gap-1">
                  {FLOW_FILTERS.map((f) => {
                    const on = flowsShown[f.key];
                    return (
                      <button
                        key={f.key}
                        type="button"
                        aria-pressed={on}
                        onClick={() =>
                          setFlowsShown((prev) => ({ ...prev, [f.key]: !prev[f.key] }))
                        }
                        className={`rounded border px-2 py-1 ${
                          on ? f.activeClass : "bg-white text-gray-400 border-gray-300"
                        }`}
                      >
                        {f.label}
                      </button>
                    );
                  })}
                </div>
                <select
                  value={categorized === null ? "" : categorized ? "yes" : "no"}
                  onChange={(event) =>
                    setCategorized(
                      event.target.value === ""
                        ? null
                        : event.target.value === "yes"
                    )
                  }
                  className="border rounded px-2 py-1"
                >
                  <option value="">Categorized & not</option>
                  <option value="yes">Categorized</option>
                  <option value="no">Uncategorized</option>
                </select>
                {monthRange && (
                  <button
                    type="button"
                    onClick={() => setMonthRange(null)}
                    className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 px-2 py-1"
                    title="Clear the chart range selection"
                  >
                    {monthRange.from === monthRange.to
                      ? monthLabel(monthRange.from)
                      : `${monthLabel(monthRange.from)} – ${monthLabel(monthRange.to)}`}
                    <span className="text-blue-400">×</span>
                  </button>
                )}
              </div>

              {selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-3 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  <span>{selected.size} selected</span>
                  <select
                    value=""
                    disabled={busy}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (!value) return;
                      classify(
                        value === "CLEAR" ? null : (value as TxnClassificationEnum)
                      );
                    }}
                    className="rounded border border-amber-300 px-2 py-1 text-xs bg-white text-amber-900 disabled:opacity-50"
                  >
                    <option value="">Reclassify as…</option>
                    {CLASSIFICATIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                    <option value="CLEAR">Clear classification</option>
                  </select>
                  <select
                    value=""
                    disabled={busy}
                    onChange={(event) => {
                      const value = event.target.value;
                      if (!value) return;
                      assignNature(value === "CLEAR" ? null : value);
                    }}
                    className="rounded border border-amber-300 px-2 py-1 text-xs bg-white text-amber-900 disabled:opacity-50"
                  >
                    <option value="">Tag nature…</option>
                    {natures.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.name}
                      </option>
                    ))}
                    <option value="CLEAR">Clear nature</option>
                  </select>
                  <div className="relative">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setReassignOpen((open) => !open)}
                      className="px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 text-xs disabled:opacity-50"
                    >
                      Reassign to…
                    </button>
                    {reassignOpen && (
                      <div className="absolute z-50 mt-1 w-72 rounded border bg-white p-2 shadow-lg text-gray-700">
                        <StakeholderCombobox
                          options={allStakeholders}
                          onPick={(choice) => reassign(choice)}
                        />
                        <label className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                          <input
                            type="checkbox"
                            checked={viaRule}
                            onChange={(event) => setViaRule(event.target.checked)}
                          />
                          also create a rule (re-attributes all matching txns)
                        </label>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={sendToWorklist}
                    className="px-2 py-1 rounded border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 text-xs disabled:opacity-50"
                  >
                    Send to worklist
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelected(new Set())}
                    className="text-xs underline hover:no-underline"
                  >
                    Clear
                  </button>
                  {busy && <span className="text-xs text-amber-700">working…</span>}
                </div>
              )}

              <div className="bg-white rounded border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase border-b">
                      <th className="px-3 py-2 w-8" />
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Account</th>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th
                        className="px-3 py-2 cursor-pointer select-none hover:text-gray-600"
                        onClick={() =>
                          setNatureSort((s) =>
                            s === "none" ? "asc" : s === "asc" ? "desc" : "none"
                          )
                        }
                        title="Sort by nature"
                      >
                        Nature
                        {natureSort === "asc" ? " ▲" : natureSort === "desc" ? " ▼" : ""}
                      </th>
                      <th className="px-3 py-2">Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((txn: Row, index: number) => (
                      <tr key={txn.txn.id} className="border-b last:border-b-0">
                        <td className="px-3 py-1.5">
                          <input
                            type="checkbox"
                            checked={selected.has(txn.txn.id)}
                            onClick={(event) => {
                              shiftHeldRef.current = event.shiftKey;
                            }}
                            onChange={() => toggleRow(txn.txn.id, index)}
                          />
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                          {txn.postingDate}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                          {txn.internal && txn.otherBankAccount ? (
                            <span className="inline-flex items-center gap-1">
                              {txn.accountNumber}
                              <span className="text-gray-300">→</span>
                              {txn.otherBankAccount.number}
                            </span>
                          ) : (
                            txn.accountNumber
                          )}
                        </td>
                        <td className="px-3 py-1.5 max-w-md" title={txn.description}>
                          <span className="flex items-center gap-1.5">
                            {txn.classification && (
                              <span
                                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                                  CLASSIFICATION_BADGE[txn.classification] ??
                                  "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {classificationLabel(txn.classification)}
                              </span>
                            )}
                            <span className="truncate">{txn.description}</span>
                          </span>
                          <span className="mt-0.5 block">
                            <ProvenanceBadge attribution={txn.attribution} />
                          </span>
                        </td>
                        <td
                          className={`px-3 py-1.5 text-right whitespace-nowrap ${
                            txn.internal
                              ? "text-gray-400"
                              : txn.amount < 0
                              ? "text-red-700"
                              : "text-green-700"
                          }`}
                        >
                          {money2(txn.amount)}
                        </td>
                        <td className="px-3 py-1.5">
                          {txn.expenseNature ? (
                            <span className="text-xs bg-indigo-50 text-indigo-700 rounded px-1.5 py-0.5">
                              {txn.expenseNature.name}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5">
                          {txn.categoryNode ? (
                            <span className="text-xs bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5">
                              {txn.categoryNode.name}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {txns.length === 0 && (
                  <p className="p-6 text-sm text-gray-400">
                    {allTxns.length === 0
                      ? `No transactions attributed to ${stakeholder.name} in this period.`
                      : "No transactions match these filters."}
                  </p>
                )}
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
